/**
 * Form layer for the merge tool.
 * Responsible for DOM elements getter/setter.
 */
import { Roles } from "../../constants/roles";

import { FormBase } from "../../util/form-base-form";

class MergeToolForm extends FormBase {
    public static findRoot(root: ParentNode = document): HTMLElement | null {
        return new MergeToolForm(document.documentElement).queryElement(
            root,
            `[data-role="${Roles.MERGE_TOOL_MODAL}"]`,
            HTMLElement,
        );
    }

    public root: HTMLElement;

    public constructor(root: HTMLElement) {
        super();
        this.root = root;
    }

    public getRoot(): HTMLElement {
        return this.root;
    }

    public getCandidatesContainer(): HTMLElement | null {
        return this.queryElement(this.root, '[data-role="merge-tool-candidates"]', HTMLElement);
    }

    public getColumnsContainer(): HTMLElement | null {
        return this.queryElement(this.root, '[data-role="merge-tool-columns"]', HTMLElement);
    }

    public getSubmitButton(): HTMLButtonElement | null {
        return this.getButton(this.root, '[data-role="merge-tool-submit"]');
    }

    public getManualTargetInput(): HTMLInputElement | null {
        return this.getInput(this.root, '[data-role="merge-tool-manual-target-input"]');
    }

    public getManualTargetId(): number | null {
        const input = this.getManualTargetInput();
        const id = Number(input?.value || 0);
        return id > 0 ? id : null;
    }

    public getEventTarget(event: Event): Element | null {
        return this.fromEventTarget(event, Element);
    }

    public isOverlayClick(root: Event | EventTarget | Element | null | undefined): boolean {
        return !!this.closest(root, `[data-role="${Roles.MERGE_TOOL_MODAL_OVERLAY}"]`, HTMLElement);
    }

    public getMergeOpenTarget(element: Element): { entity: string; id: number } | null {
        if (!(element instanceof HTMLElement)) return null;
        const entity = element.dataset.mergeEntity;
        const id = Number(element.dataset.mergeId || 0);
        return entity && id > 0 ? { entity, id } : null;
    }

    public getCandidateId(element: Element): number | null {
        if (!(element instanceof HTMLElement)) return null;
        const id = Number(element.dataset.candidateId || 0);
        return id > 0 ? id : null;
    }

    public getFieldSelection(element: Element): { field: string; side: "source" | "target" } | null {
        if (!(element instanceof HTMLElement)) return null;
        const field = element.dataset.field;
        const side = element.dataset.side;
        if (!field || (side !== "source" && side !== "target")) return null;
        return { field, side };
    }
}

export { MergeToolForm };
